// ─── Types (mirrors packages/api/src/strategy/condition-engine/condition-engine.types.ts) ───

export type ValueRefType = 'CONSTANT' | 'PRICE' | 'INDICATOR' | 'POSITION' | 'ACCOUNT' | 'TIME';

export interface ValueRef {
  type: ValueRefType;
  value?: number;
  field?: string;
  indicator?: string;
  params?: { period?: number; stdDev?: number; source?: string; fastPeriod?: number; slowPeriod?: number; signalPeriod?: number; kPeriod?: number; acceleration?: number; maxAcceleration?: number; multiplier?: number; conversionPeriod?: number; basePeriod?: number; spanPeriod?: number; displacement?: number };
  offset?: number;
}

export type ComparisonOp = '>' | '<' | '>=' | '<=' | '==' | 'CROSSES_ABOVE' | 'CROSSES_BELOW';

export interface ComparisonCondition {
  type: 'COMPARISON';
  left: ValueRef;
  op: ComparisonOp;
  right: ValueRef;
}

export interface ConditionGroup {
  type: 'AND' | 'OR';
  conditions: Condition[];
}

export type Condition = ComparisonCondition | ConditionGroup;

// Lot sizing
export interface FixedLotSizing { type: 'FIXED'; volume: number }
export interface RiskBasedLotSizing { type: 'RISK_BASED'; riskPct: number }
export interface EquityPctLotSizing { type: 'EQUITY_PCT'; pct: number }
export interface MartingaleLotSizing { type: 'MARTINGALE'; baseLotRiskFactor: number; multiplier: number }
export type LotSizing = FixedLotSizing | RiskBasedLotSizing | EquityPctLotSizing | MartingaleLotSizing;

export interface EntryRule {
  id: string;
  direction: 'BUY' | 'SELL' | 'SIGNAL';
  conditions: Condition;
  buyCondition?: Condition;
  sellCondition?: Condition;
  lotSizing: LotSizing;
  cooldownCandles?: number;
  maxPositions?: number;
  orderType?: 'MARKET' | 'LIMIT';
  limitOffsetPips?: number;
}

export interface GridConfig {
  enabled: boolean;
  direction: 'BUY' | 'SELL' | 'FOLLOW_ENTRY';
  stepType: 'ABSOLUTE' | 'PERCENT' | 'ATR_MULTIPLE';
  stepValue: number;
  maxGridLevels: number;
  lotSizing: LotSizing;
}

export interface ExitRule {
  id: string;
  name: string;
  conditions: Condition;
  action: 'CLOSE_ALL' | 'CLOSE_OLDEST' | 'CLOSE_NEWEST' | 'CLOSE_PARTIAL';
  closePct?: number;         // For CLOSE_PARTIAL: fraction of volume to close (0-1)
  forEntryRules?: string[];  // If set, only targets positions from these entry rules
}

export interface TrailingStopConfig {
  enabled: boolean;
  type: 'ATR_MULTIPLE' | 'PERCENT' | 'FIXED_DISTANCE';
  value: number;
  atrPeriod?: number;
  basketMode?: boolean;
}

export interface ConditionEngineRules {
  strategy_type: string;
  market_data: { timeframe: string };
  entry: { rules: EntryRule[]; grid?: GridConfig };
  exit: { rules: ExitRule[]; trailingStop?: TrailingStopConfig; equityStopLossPct: number };
  risk: { riskFactor: number; maxTotalPositions: number };
}

// ─── Constants ──────────────────────────────────────────

export const TIMEFRAMES = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'] as const;

export const INDICATOR_VALUES = [
  // Moving Averages
  'RSI', 'SMA', 'EMA', 'WMA', 'DEMA', 'TEMA', 'HMA',
  // Volatility
  'ATR', 'BB_UPPER', 'BB_MIDDLE', 'BB_LOWER', 'BB_STD',
  'KELTNER_UPPER', 'KELTNER_MID', 'KELTNER_LOWER',
  'DONCHIAN_UPPER', 'DONCHIAN_LOWER', 'DONCHIAN_MID',
  'CHANDELIER_EXIT_LONG', 'CHANDELIER_EXIT_SHORT',
  // Range & Channel
  'RANGE_HIGH', 'RANGE_LOW', 'RANGE_WIDTH',
  'HIGHEST_HIGH', 'LOWEST_LOW',
  // Volume
  'VOLUME_SMA', 'OBV', 'VWAP', 'MFI', 'CMF', 'ADL', 'FORCE_INDEX',
  // MACD Family
  'MACD', 'MACD_SIGNAL', 'MACD_HISTOGRAM', 'PPO',
  // Stochastic Family
  'STOCH_K', 'STOCH_D', 'STOCH_RSI',
  // Oscillators
  'CCI', 'ADX', 'WILLIAMS_R', 'ROC', 'MOMENTUM', 'AWESOME_OSC',
  'TRIX', 'DPO', 'CMO', 'ULTIMATE_OSC', 'COPPOCK',
  // Directional
  'DMI_PLUS', 'DMI_MINUS',
  'AROON_UP', 'AROON_DOWN', 'AROON_OSC',
  // Trend
  'PSAR', 'SUPERTREND', 'KST', 'LINEAR_REG_SLOPE',
  // Price Derivatives
  'MEDIAN_PRICE', 'TYPICAL_PRICE', 'WEIGHTED_CLOSE',
  // Ichimoku
  'ICHIMOKU_TENKAN', 'ICHIMOKU_KIJUN', 'ICHIMOKU_SENKOU_A',
  'ICHIMOKU_SENKOU_B', 'ICHIMOKU_CHIKOU',
  // Elder Ray
  'ELDER_RAY_BULL', 'ELDER_RAY_BEAR',
  // Pivot Points
  'PIVOT_PP', 'PIVOT_R1', 'PIVOT_S1', 'PIVOT_R2', 'PIVOT_S2',
  // Candle Analysis
  'CANDLE_RANGE', 'CANDLE_BODY', 'SPIKE_RATIO',
] as const;

/** Human-readable labels for indicators that need them */
const INDICATOR_LABELS: Partial<Record<string, string>> = {
  BB_UPPER: 'BB Upper', BB_MIDDLE: 'BB Middle', BB_LOWER: 'BB Lower', BB_STD: 'BB StdDev',
  KELTNER_UPPER: 'Keltner Upper', KELTNER_MID: 'Keltner Mid', KELTNER_LOWER: 'Keltner Lower',
  DONCHIAN_UPPER: 'Donchian Upper', DONCHIAN_LOWER: 'Donchian Lower', DONCHIAN_MID: 'Donchian Mid',
  CHANDELIER_EXIT_LONG: 'Chandelier Long', CHANDELIER_EXIT_SHORT: 'Chandelier Short',
  RANGE_HIGH: 'Range High', RANGE_LOW: 'Range Low', RANGE_WIDTH: 'Range Width',
  HIGHEST_HIGH: 'Highest High', LOWEST_LOW: 'Lowest Low',
  VOLUME_SMA: 'Volume SMA', FORCE_INDEX: 'Force Index',
  MACD_SIGNAL: 'MACD Signal', MACD_HISTOGRAM: 'MACD Histogram',
  STOCH_K: 'Stoch %K', STOCH_D: 'Stoch %D', STOCH_RSI: 'Stoch RSI',
  WILLIAMS_R: 'Williams %R', AWESOME_OSC: 'Awesome Osc', ULTIMATE_OSC: 'Ultimate Osc',
  DMI_PLUS: '+DI', DMI_MINUS: '−DI',
  AROON_UP: 'Aroon Up', AROON_DOWN: 'Aroon Down', AROON_OSC: 'Aroon Osc',
  LINEAR_REG_SLOPE: 'LinReg Slope',
  MEDIAN_PRICE: 'Median Price', TYPICAL_PRICE: 'Typical Price', WEIGHTED_CLOSE: 'Wtd Close',
  ICHIMOKU_TENKAN: 'Ichimoku Tenkan', ICHIMOKU_KIJUN: 'Ichimoku Kijun',
  ICHIMOKU_SENKOU_A: 'Senkou A', ICHIMOKU_SENKOU_B: 'Senkou B', ICHIMOKU_CHIKOU: 'Chikou',
  ELDER_RAY_BULL: 'Elder Bull', ELDER_RAY_BEAR: 'Elder Bear',
  PIVOT_PP: 'Pivot PP', PIVOT_R1: 'Pivot R1', PIVOT_S1: 'Pivot S1', PIVOT_R2: 'Pivot R2', PIVOT_S2: 'Pivot S2',
  CANDLE_RANGE: 'Candle Range', CANDLE_BODY: 'Candle Body', SPIKE_RATIO: 'Spike Ratio',
};

/** Short descriptions for indicators — shown as hints in the editor */
export const INDICATOR_DESCRIPTIONS: Partial<Record<string, string>> = {
  RSI: 'Momentum oscillator (0–100). <30 = oversold, >70 = overbought',
  SMA: 'Simple moving average of close prices',
  EMA: 'Exponential moving average — faster response than SMA',
  ATR: 'Average True Range — measures volatility in price points',
  ADX: 'Trend strength (0–100). >25 = trending, <20 = ranging',
  MACD: 'Trend momentum. Positive = bullish, negative = bearish',
  MACD_HISTOGRAM: 'MACD minus signal line. Crossing zero = momentum shift',
  BB_UPPER: 'Upper Bollinger Band — price near/above = overbought',
  BB_LOWER: 'Lower Bollinger Band — price near/below = oversold',
  STOCH_K: 'Stochastic oscillator (0–100). <20 = oversold, >80 = overbought',
  CCI: 'Commodity Channel Index. >100 = overbought, <-100 = oversold',
  PSAR: 'Parabolic SAR — dots below price = uptrend, above = downtrend',
  SUPERTREND: 'Trend direction line. Price above = buy, below = sell',
  VWAP: 'Volume-weighted average price. Price above = bullish bias',
  OBV: 'On-Balance Volume — rising = accumulation, falling = distribution',
  CANDLE_RANGE: 'Current candle high minus low — total price movement within the candle',
  CANDLE_BODY: 'Current candle |close - open| — directional movement, ignoring wicks',
  SPIKE_RATIO: 'Current range / avg range of N candles. >2 = spike detected. Use to filter out volatile spikes',
};

export const INDICATORS: { value: string; label: string }[] = INDICATOR_VALUES.map((v) => ({
  value: v,
  label: INDICATOR_LABELS[v] ?? v,
}));

export const PRICE_FIELDS = ['CURRENT', 'OPEN', 'HIGH', 'LOW', 'CLOSE', 'VOLUME', 'PREV_TICK_HIGH', 'PREV_TICK_LOW'] as const;

export const POSITION_FIELDS = [
  'COUNT', 'FLOATING_PNL', 'FLOATING_PNL_PCT', 'PNL_PER_LOT',
  'LAST_ENTRY_PRICE', 'AVG_ENTRY_PRICE', 'BASKET_VOLUME',
  'TICKS_SINCE_ENTRY', 'CANDLES_SINCE_ENTRY',
] as const;

export const ACCOUNT_FIELDS = ['EQUITY', 'BALANCE', 'ASSIGNED_CAPITAL'] as const;

export const TIME_FIELDS = ['HOUR', 'MINUTE', 'DAY_OF_WEEK', 'MONTH'] as const;

export const OPS: ComparisonOp[] = ['>', '<', '>=', '<=', '==', 'CROSSES_ABOVE', 'CROSSES_BELOW'];

export const OP_LABELS: Record<string, string> = {
  '>': '>', '<': '<', '>=': '\u2265', '<=': '\u2264', '==': '=',
  CROSSES_ABOVE: 'Crosses Above', CROSSES_BELOW: 'Crosses Below',
};

// ─── Default Factories ──────────────────────────────────

export const DEFAULT_VALUE_REFS: Record<ValueRefType, ValueRef> = {
  CONSTANT: { type: 'CONSTANT', value: 0 },
  PRICE: { type: 'PRICE', field: 'CURRENT' },
  INDICATOR: { type: 'INDICATOR', indicator: 'RSI', params: { period: 14 } },
  POSITION: { type: 'POSITION', field: 'COUNT' },
  ACCOUNT: { type: 'ACCOUNT', field: 'EQUITY' },
  TIME: { type: 'TIME', field: 'HOUR' },
};

export const DEFAULT_LOT_SIZING: Record<string, LotSizing> = {
  FIXED: { type: 'FIXED', volume: 0.01 },
  MARTINGALE: { type: 'MARTINGALE', baseLotRiskFactor: 1, multiplier: 2 },
  RISK_BASED: { type: 'RISK_BASED', riskPct: 2 },
  EQUITY_PCT: { type: 'EQUITY_PCT', pct: 5 },
};

export const DEFAULT_COMPARISON: ComparisonCondition = {
  type: 'COMPARISON',
  left: { type: 'PRICE', field: 'CURRENT' },
  op: '>',
  right: { type: 'CONSTANT', value: 0 },
};

export function makeEntryRule(index: number): EntryRule {
  return {
    id: `entry_${index + 1}`,
    direction: 'BUY',
    conditions: structuredClone(DEFAULT_COMPARISON),
    lotSizing: { type: 'FIXED', volume: 0.01 },
  };
}

export function makeExitRule(index: number): ExitRule {
  return {
    id: `exit_${index + 1}`,
    name: 'New Exit Rule',
    conditions: {
      type: 'COMPARISON',
      left: { type: 'POSITION', field: 'FLOATING_PNL' },
      op: '>=',
      right: { type: 'CONSTANT', value: 5 },
    },
    action: 'CLOSE_ALL',
  };
}

// ─── Path Helpers ───────────────────────────────────────

function toKey(k: string): string | number {
  const n = Number(k);
  return Number.isInteger(n) && n >= 0 ? n : k;
}

export function getByPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce((cur: unknown, k) => {
    if (cur == null) return undefined;
    return (cur as Record<string, unknown>)[k as string] ?? (cur as unknown[])[toKey(k) as number];
  }, obj);
}

export function setByPathImmutable<T>(obj: T, path: string, value: unknown): T {
  const keys = path.split('.');
  const result = Array.isArray(obj) ? ([...obj] as unknown as T) : { ...obj };
  let current: unknown = result;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = toKey(keys[i]!);
    const child = (current as Record<string | number, unknown>)[key];
    const cloned = Array.isArray(child) ? [...child] : { ...(child as Record<string, unknown>) };
    (current as Record<string | number, unknown>)[key] = cloned;
    current = cloned;
  }

  const lastKey = toKey(keys[keys.length - 1]!);
  (current as Record<string | number, unknown>)[lastKey] = value;
  return result;
}

// ─── Reducer ────────────────────────────────────────────

export type DraftAction =
  | { type: 'INIT'; payload: ConditionEngineRules }
  | { type: 'SET_PATH'; path: string; value: unknown }
  | { type: 'SET_PATH_NUM'; path: string; value: string }
  | { type: 'ADD_ENTRY_RULE' }
  | { type: 'REMOVE_ENTRY_RULE'; index: number }
  | { type: 'ADD_EXIT_RULE' }
  | { type: 'REMOVE_EXIT_RULE'; index: number }
  | { type: 'ADD_CONDITION'; groupPath: string }
  | { type: 'ADD_GROUP'; groupPath: string }
  | { type: 'REMOVE_CONDITION'; groupPath: string; index: number }
  | { type: 'WRAP_IN_GROUP'; path: string }
  | { type: 'TOGGLE_GROUP_TYPE'; path: string }
  | { type: 'CHANGE_VALUE_REF_TYPE'; path: string; newType: ValueRefType }
  | { type: 'CHANGE_LOT_TYPE'; path: string; newType: string }
  | { type: 'CHANGE_DIRECTION'; ruleIndex: number; direction: 'BUY' | 'SELL' | 'SIGNAL' }
  | { type: 'UNWRAP_GROUP'; path: string };

export function draftReducer(state: ConditionEngineRules | null, action: DraftAction): ConditionEngineRules | null {
  if (action.type === 'INIT') return structuredClone(action.payload);
  if (!state) return state;

  switch (action.type) {
    case 'SET_PATH':
      return setByPathImmutable(state, action.path, action.value);

    case 'SET_PATH_NUM': {
      const n = parseFloat(action.value);
      if (isNaN(n)) return state;
      return setByPathImmutable(state, action.path, n);
    }

    case 'ADD_ENTRY_RULE': {
      const rules = [...state.entry.rules, makeEntryRule(state.entry.rules.length)];
      return { ...state, entry: { ...state.entry, rules } };
    }

    case 'REMOVE_ENTRY_RULE': {
      const rules = state.entry.rules.filter((_, i) => i !== action.index);
      return { ...state, entry: { ...state.entry, rules } };
    }

    case 'ADD_EXIT_RULE': {
      const rules = [...state.exit.rules, makeExitRule(state.exit.rules.length)];
      return { ...state, exit: { ...state.exit, rules } };
    }

    case 'REMOVE_EXIT_RULE': {
      const rules = state.exit.rules.filter((_, i) => i !== action.index);
      return { ...state, exit: { ...state.exit, rules } };
    }

    case 'ADD_CONDITION': {
      const group = getByPath(state, action.groupPath) as ConditionGroup;
      const newConds = [...group.conditions, structuredClone(DEFAULT_COMPARISON)];
      return setByPathImmutable(state, `${action.groupPath}.conditions`, newConds);
    }

    case 'ADD_GROUP': {
      const group = getByPath(state, action.groupPath) as ConditionGroup;
      const newGroup: ConditionGroup = { type: 'AND', conditions: [structuredClone(DEFAULT_COMPARISON)] };
      const newConds = [...group.conditions, newGroup];
      return setByPathImmutable(state, `${action.groupPath}.conditions`, newConds);
    }

    case 'REMOVE_CONDITION': {
      const group = getByPath(state, action.groupPath) as ConditionGroup;
      let newConds = group.conditions.filter((_, i) => i !== action.index);
      if (newConds.length === 0) newConds = [structuredClone(DEFAULT_COMPARISON)];
      return setByPathImmutable(state, `${action.groupPath}.conditions`, newConds);
    }

    case 'WRAP_IN_GROUP': {
      const current = getByPath(state, action.path) as Condition;
      const clone = structuredClone(current);
      const wrapped: ConditionGroup = { type: 'AND', conditions: [clone] };
      return setByPathImmutable(state, action.path, wrapped);
    }

    case 'TOGGLE_GROUP_TYPE': {
      const group = getByPath(state, action.path) as ConditionGroup;
      return setByPathImmutable(state, `${action.path}.type`, group.type === 'AND' ? 'OR' : 'AND');
    }

    case 'CHANGE_VALUE_REF_TYPE':
      return setByPathImmutable(state, action.path, structuredClone(DEFAULT_VALUE_REFS[action.newType]));

    case 'CHANGE_LOT_TYPE':
      return setByPathImmutable(state, action.path, structuredClone(DEFAULT_LOT_SIZING[action.newType]));

    case 'UNWRAP_GROUP': {
      const group = getByPath(state, action.path) as ConditionGroup;
      if (group.conditions.length === 1) {
        return setByPathImmutable(state, action.path, structuredClone(group.conditions[0]));
      }
      return state;
    }

    case 'CHANGE_DIRECTION': {
      const rule = state.entry.rules[action.ruleIndex]!;
      const updated = { ...rule, direction: action.direction };
      if (action.direction === 'SIGNAL') {
        if (!updated.buyCondition) {
          updated.buyCondition = { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'RSI', params: { period: 14 } }, op: '<', right: { type: 'CONSTANT', value: 40 } };
        }
        if (!updated.sellCondition) {
          updated.sellCondition = { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'RSI', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: 60 } };
        }
      }
      const rules = state.entry.rules.map((r, i) => i === action.ruleIndex ? updated : r);
      return { ...state, entry: { ...state.entry, rules } };
    }

    default:
      return state;
  }
}
